import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useRef, useState } from "react";
import FilterPopup from "./FilterPopup";
import styles from "./ContentManager.module.css";
import {
    getBlogLinks,
    getChowLinks,
    getFslPodcastLinks,
    getPodcastLinks,
    getSourcesByIds,
    getSourcesMetadata,
    startSync,
    updateRecordStatus
} from "../../api/axiosApi";
import { Button, Spinner, Switch, Checkbox, Tooltip } from "@material-tailwind/react";
import { BsFilterSquare } from "react-icons/bs";
import { FiRefreshCw } from "react-icons/fi"; // Add this import for the icon
import { MEDIA_TYPES, STATUS_TYPES } from "./FilterOptions";

const MEDIA_TYPE_LABELS = MEDIA_TYPES.reduce(
    (acc, curr) => {
        acc[curr.value] = curr.label;
        return acc;
    },
    {} as Record<string, string>
);

const STATUS_LABELS = STATUS_TYPES.reduce(
    (acc, curr) => {
        acc[curr.value] = curr.label;
        return acc;
    },
    {} as Record<string, string>
);

// Reusable date comparator for sorting
const dateComparator = (valueA: any, valueB: any) => {
    if (!valueA && !valueB) return 0;
    if (!valueA) return -1;
    if (!valueB) return 1;
    return new Date(valueA).getTime() - new Date(valueB).getTime();
};

const formattedColumnDefs: ColDef[] = [
    {
        field: "type",
        headerName: "Media Type",
        width: 150,
        filter: false,
        valueFormatter: (params: any) => MEDIA_TYPE_LABELS[params.value] || params.value
    },
    {
        field: "title",
        headerName: "Title",
        width: 600,
        filter: false,
        cellRenderer: (params: any) => {
            const { type, title, link } = params.data;
            return (
                <a href={link} target="_blank" rel="noopener noreferrer" className={styles.linkCell}>
                    {(type === "sps_podcast" || type === "fsl_podcast") && title === "[Private video]" ? link : title}
                </a>
            );
        }
    },
    {
        field: "status",
        headerName: "Status",
        width: 160,
        filter: false,
        cellRenderer: (params: any) => {
            const status = params.value;
            const label = STATUS_LABELS[status] || status;
            if (status === "failed") {
                return (
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {label}
                        <span
                            style={{ display: "flex", alignItems: "center", color: "#1976d2", cursor: "pointer", fontWeight: 500 }}
                            onClick={async e => {
                                e.stopPropagation();
                                try {
                                    const res = await updateRecordStatus(params.data.link, params.data.type, "waiting");
                                    if (res.data.status === "success") {
                                        params.node.setData({ ...params.data, status: "waiting", failure_log: "" });
                                    }
                                } catch (error) {
                                    console.error("Failed to update status:", error);
                                }
                            }}
                        >
                            <FiRefreshCw style={{ marginRight: 4 }} />
                            try again
                        </span>
                    </span>
                );
            }
            return label;
        }
    },
    {
        field: "published_time",
        headerName: "Published Date",
        width: 160,
        filter: false,
        valueFormatter: (params: any) => {
            if (!params.value) return "";
            const date = new Date(params.value);
            return date
                .toLocaleString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    timeZone: "Asia/Kolkata"
                })
                .replace(/ /g, " ");
        },
        comparator: dateComparator
    },
    {
        field: "ingested_time",
        headerName: "Ingested Date",
        width: 160,
        filter: false,
        valueFormatter: (params: any) => {
            if (!params.value) return "";
            const date = new Date(params.value);
            return date
                .toLocaleString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    timeZone: "Asia/Kolkata"
                })
                .replace(/ /g, " ");
        },
        comparator: dateComparator
    },
    {
        headerName: "Suspend",
        field: "suspend",
        width: 120,
        filter: false,
        cellRenderer: (params: any) => {
            const status = params.data.status;
            if (status === "waiting" || status === "suspended") {
                const isSuspended = status === "suspended";
                return (
                    <Checkbox
                        checked={isSuspended}
                        onChange={() => params.context.onSuspendToggle(params.data)}
                        ripple={false}
                        color="blue"
                        crossOrigin={undefined}
                        className={`align-middle  ${isSuspended ? "bg-blue-500 border-blue-500" : ""}`}
                    />
                );
            }
            return null;
        }
    },
    {
        field: "failure_log",
        headerName: "Failure Log",
        width: 250,
        filter: false,
        cellRenderer: (params: any) => (
            <Tooltip
                content={<div style={{ whiteSpace: "pre-line", maxWidth: 300, wordBreak: "break-word" }}>{params.value || ""}</div>}
                placement="top"
                className="z-50"
            >
                <span
                    style={{
                        color: "#e53935",
                        fontSize: 12,
                        cursor: params.value ? "pointer" : "default",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 220,
                        display: "inline-block"
                    }}
                >
                    {params.value || ""}
                </span>
            </Tooltip>
        )
    }
    // ...other columns if any...
];

const ContentManager: React.FC = () => {
    const [rowData, setRowData] = useState<any[]>([]);
    const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
    const [showFilter, setShowFilter] = useState(false);
    // Change status to array of string, default to ["failed"]
    const [filters, setFilters] = useState<{ mediaType: string; status: string[] }>({ mediaType: "", status: ["all"] });
    const [isFetchingUpdates, setIsFetchingUpdates] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [statusMessage, setStatusMessage] = useState<string>("");
    const filterBtnRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setColumnDefs(formattedColumnDefs);
        getContentMetadata();
    }, []);

    // Pass status as array to getSourcesMetadata
    const getContentMetadata = async (filterVals = filters) => {
        const data = await getSourcesMetadata(filterVals.mediaType, filterVals.status.includes("all") ? [] : filterVals.status);
        const contentData = data.data.sources;
        setRowData(contentData);
        return contentData;
    };

    async function onSync(event: React.MouseEvent<HTMLButtonElement>): Promise<void> {
        setIsSyncing(true);
        setStatusMessage("Syncing is in progress...");

        const waitingFilter = { mediaType: "", status: ["waiting"] };
        setFilters(waitingFilter);
        const records = await getContentMetadata(waitingFilter);
        // Collect IDs of the fetched records
        const recordIds = records.map((record: any) => record.id);

        try {
            const res = await startSync();
            console.log("Sync started:", res);

            let isFirstPoll = true;
            let pollInterval: any;

            const poll = async () => {
                try {
                    const data = recordIds.length ? await getSourcesByIds(recordIds) : { data: { sources: [] } };
                    setRowData(data.data.sources);
                    const processingRecords = data.data.sources.filter((source: any) => source.status === "processing");

                    if (!processingRecords || processingRecords.length === 0) {
                        if (isFirstPoll) {
                            // Don't clear interval on first poll, just set flag
                            isFirstPoll = false;
                        } else {
                            clearInterval(pollInterval);
                            setIsSyncing(false);
                            setStatusMessage("Sync is completed.");
                            // getContentMetadata();
                            setTimeout(() => setStatusMessage(""), 3000);
                        }
                    // } else {
                    //     isFirstPoll = false;
                    }
                } catch (error) {
                    console.error("Error in fetching records:", error);
                    clearInterval(pollInterval);
                    setIsSyncing(false);
                    setStatusMessage("Sync failed. Please try again.");
                    setTimeout(() => setStatusMessage(""), 3000);
                }
            };

            // setTimeout(()=>poll(),5000); // Initial poll
            poll();
            pollInterval = setInterval(poll, 5000);
        } catch (error) {
            console.error("Error starting sync:", error);
            setIsSyncing(false);
            setStatusMessage("Sync failed. Please try again.");
            setTimeout(() => setStatusMessage(""), 3000);
        }
    }

    async function onFetchUpdates(event: React.MouseEvent<HTMLButtonElement>): Promise<void> {
        setIsFetchingUpdates(true);
        setStatusMessage("Checking for updates...");
        // Set filter to Waiting status
        const waitingFilter = { mediaType: "", status: ["waiting"] };
        setFilters(waitingFilter);
        await getContentMetadata(waitingFilter);

        try {
            console.log("onFetchUpdates started:");
            const podcasts = await getPodcastLinks();
            await getContentMetadata(waitingFilter);
            const fsl_podcasts = await getFslPodcastLinks();
            await getContentMetadata(waitingFilter);
            const chows = await getChowLinks();
            await getContentMetadata(waitingFilter);
            const blogs = await getBlogLinks();
            await getContentMetadata(waitingFilter);
            // console.log("Fetched podcasts links:", podcasts);
            // console.log("Fetched fsl_podcasts links:", fsl_podcasts);
            // console.log("Fetched chow links:", chows);
            // console.log("Fetched blog links:", blogs);
            setStatusMessage("Checking for updates is completed.");
            // Clear the success message after 3 seconds
            setTimeout(() => setStatusMessage(""), 3000);
        } catch (error) {
            console.error("Error checking for updates:", error);
            setStatusMessage("Failed to check for updates. Please try again.");
            setTimeout(() => setStatusMessage(""), 3000);
        } finally {
            setIsFetchingUpdates(false);
            // Fetch with waiting filter
            // getContentMetadata(waitingFilter);
        }
    }

    // Handler for suspend toggle
    const handleSuspendToggle = async (row: any) => {
        const newStatus = row.status === "waiting" ? "suspended" : "waiting";
        try {
            const res = await updateRecordStatus(row.link, row.type, newStatus); // Call your API to update status
            res.data.status === "success" && setRowData(prev => prev.map(item => (item.id === row.id ? { ...item, status: newStatus } : item)));
            console.log(`Toggled suspend for id=${row.id}, new status=${newStatus}`);
        } catch (error) {
            console.error("Failed to update status:", error);
            // Optionally show an error message to the user
        }
    };

    // Update handleFilterClose to accept status as array
    const handleFilterClose = (selected: { mediaType: string; status: string[] }) => {
        setShowFilter(false);
        setFilters(selected);
        getContentMetadata(selected);
    };

    const pinnedBottomRowData = [
        {
            type: `Row count: ${rowData.length}`,
            title: "",
            suspend: "",
            status: "",
            marked_for_removal: ""
        }
    ];

    return (
        <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 16, marginTop: 16 }}>
                <Button
                    onClick={onFetchUpdates}
                    disabled={isFetchingUpdates || isSyncing}
                    className="bg-blue-400 text-white font-semibold px-6 py-2 rounded flex items-center gap-2"
                    style={{
                        cursor: isFetchingUpdates || isSyncing ? "not-allowed" : "pointer"
                    }}
                >
                    {isFetchingUpdates && <Spinner className="h-3 w-3" style={{ color: "white" }} />}
                    Check for Updates
                </Button>
                <Button
                    onClick={onSync}
                    disabled={isFetchingUpdates || isSyncing}
                    className="bg-blue-400 text-white font-semibold px-6 py-2 rounded flex items-center gap-2"
                    style={{
                        cursor: isFetchingUpdates || isSyncing ? "not-allowed" : "pointer",
                        marginLeft: 16
                    }}
                >
                    {isSyncing && <Spinner className="h-3 w-3" style={{ color: "white" }} />}
                    Sync
                </Button>
                <div ref={filterBtnRef} style={{ display: "inline-block" }}>
                    <BsFilterSquare
                        size={28}
                        style={{
                            color: isFetchingUpdates || isSyncing ? "#cbd5e1" : "#64748b",
                            cursor: isFetchingUpdates || isSyncing ? "not-allowed" : "pointer",
                            marginLeft: 16
                        }}
                        title="Filter"
                        onClick={() => !(isFetchingUpdates || isSyncing) && setShowFilter(true)}
                    />
                </div>
                <div style={{ marginLeft: 8, fontSize: 12, color: "#555" }}>
                    <div>
                        <span style={{ fontWeight: 500 }}>Media Type:</span> {filters.mediaType ? MEDIA_TYPE_LABELS[filters.mediaType] : "All"}
                    </div>
                    <div>
                        <span style={{ fontWeight: 500 }}>Status:</span>{" "}
                        {filters.status.includes("all") ? "All" : filters.status.map(st => STATUS_LABELS[st] || st).join(", ")}
                    </div>
                </div>
            </div>
            {statusMessage && (
                <div
                    style={{
                        padding: "12px 16px",
                        marginBottom: 16,
                        backgroundColor: statusMessage.includes("completed")
                            ? "#e8f5e9"
                            : statusMessage.includes("failed") || statusMessage.includes("Failed")
                            ? "#ffebee"
                            : "#e3f2fd",
                        color: statusMessage.includes("completed")
                            ? "#2e7d32"
                            : statusMessage.includes("failed") || statusMessage.includes("Failed")
                            ? "#c62828"
                            : "#1565c0",
                        borderRadius: 4,
                        fontSize: 14,
                        fontWeight: 500,
                        display: "flex",
                        alignItems: "center",
                        gap: 8
                    }}
                >
                    {(isFetchingUpdates || isSyncing) && <Spinner className="h-4 w-4" />}
                    {statusMessage}
                </div>
            )}
            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <div className="ag-theme-quartz" style={{ width: "100%", flex: 1 }}>
                    <AgGridReact
                        rowData={rowData}
                        columnDefs={columnDefs}
                        context={{ onSuspendToggle: handleSuspendToggle }}
                        getRowId={params => String(params.data.id)}
                        defaultColDef={{
                            flex: 0,
                            filter: true,
                            editable: false,
                            resizable: true
                        }}
                        enableCellTextSelection={true}
                        noRowsOverlayComponent={() => <div style={{ padding: 24, textAlign: "center", color: "#888" }}>No data to show</div>}
                        pinnedBottomRowData={pinnedBottomRowData}
                    />
                </div>
            </div>
            {/* Pass status as array to FilterPopup */}
            <FilterPopup open={showFilter} onClose={handleFilterClose} anchorRef={filterBtnRef} mediaType={filters.mediaType} status={filters.status} />
        </div>
    );
};

export default ContentManager;
